<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Dashboard" icon="fa-home"/>

            <div class="start fadeIn">
                <div class="welcome">
                    <h1><strong>{{ welcome }},</strong> {{ currentUser.first_name }}</h1>
                    <h3>What can we help you with today?</h3>
                </div>

                <!-- TPO Onboarding Alert - Not Yet Started -->
                <div v-if="needsOnboarding" class="tpo-onboarding-alert fadeIn">
                    <div class="alert-inner">
                        <div class="alert-content">
                            <div class="alert-icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </div>
                            <div class="alert-body">
                                <h2><strong>Action Required:</strong> Complete Your Payment Plan Setup</h2>
                                <p>You have a payment plan that needs to be set up with our payment partner, The Payment Option. Please complete the onboarding process to activate your plan.</p>
                                <div class="alert-actions">
                                    <a :href="tpoOnboardingUrl" target="_blank" class="btn-primary">
                                        <i class="fas fa-arrow-right mr-2"></i>Complete Setup Now
                                    </a>
                                    <a href="mailto:help@my247ed.com?subject=No Longer Interested in Payment Plan&body=I am no longer interested in completing my payment plan setup. Please contact me to discuss my options." class="btn-secondary">
                                        I'm no longer interested...
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- TPO DocuSign Alert - Onboarding Started -->
                <div v-else-if="needsDocuSign" class="tpo-onboarding-alert fadeIn">
                    <div class="alert-inner">
                        <div class="alert-content">
                            <div class="alert-icon">
                                <i class="fas fa-envelope"></i>
                            </div>
                            <div class="alert-body">
                                <h2><strong>Action Required:</strong> Complete Your Payment Plan Agreement</h2>
                                <p>Please check your e-mail for your agreement from DocuSign from the lending partner The Payment Option. Contact us if you have not received this document.</p>
                                <div class="alert-actions">
                                    <a href="mailto:help@my247ed.com?subject=Haven't Received DocuSign Agreement&body=I have not received the DocuSign agreement from The Payment Option for my payment plan. Please assist me with this matter." class="btn-primary">
                                        <i class="fas fa-life-ring mr-2"></i>Contact Support
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Cart Enrollment Alert -->
                <div v-if="hasCartEnrollment" class="tpo-onboarding-alert cart-alert fadeIn">
                    <div class="alert-inner">
                        <div class="alert-content">
                            <div class="alert-icon">
                                <i class="fad fa-shopping-cart"></i>
                            </div>
                            <div class="alert-body">
                                <h2><strong>Action Required:</strong> Complete Your Cart Enrollment</h2>
                                <p><span v-if="cartQuote.reseller_name"><strong>{{ cartQuote.reseller_name }}</strong> has prepared </span><span v-else>You have </span><strong>{{ courseCount }} {{ courseCount === 1 ? 'course' : 'courses' }}</strong> ({{ cartQuote.hours }} clock hours) for you and they are ready for checkout for <strong>{{ cartQuote.total | currency }}</strong>.</p>
                                <div class="alert-actions">
                                    <router-link to="/cart-checkout" class="btn-primary">
                                        <i class="fas fa-credit-card mr-2"></i>Complete Checkout
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Unpaid Sales Tax Alert -->
                <div v-if="hasUnpaidSalesTax" class="tpo-onboarding-alert tax-alert fadeIn">
                    <div class="alert-inner">
                        <div class="alert-content">
                            <div class="alert-icon">
                                <i class="fas fa-exclamation-triangle"></i>
                            </div>
                            <div class="alert-body">
                                <h2><strong>Action Required:</strong> Sales Tax Payment Due</h2>
                                <p>The Washington state legislature enacted a retroactive sales tax on existing contracts with any existing balance as of October 1, 2025, regardless whether your payment plan has started or not started. Due to this sales tax change, sales tax must now be collected.</p>
                                <p>Please refer to the email notification of your Washington State Sales Tax Payment sent September, 2025 for more details.</p>
                                <p>Sales tax is a separate transaction from any regular or additional payment, and can be made with a credit card. Payment is made at our lending partner, The Payment Option. Click the link below to login to your account and pay your sales tax.</p>
                                <div class="alert-actions">
                                    <a href="https://app.thepaymentoption.com" target="_blank" class="btn-primary">
                                        <i class="fas fa-credit-card mr-2"></i>Login to Pay Sales Tax
                                    </a>
                                    <a href="mailto:support@my247ed.com?subject=Sales Tax Question" class="btn-secondary">
                                        <i class="fas fa-envelope mr-2"></i>Contact Support
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Expired Courses Alert -->
                <div v-if="hasExpiredCourses" class="tpo-onboarding-alert expired-alert fadeIn">
                    <div class="alert-inner">
                        <div class="alert-content">
                            <div class="alert-icon">
                                <i class="fas fa-clock"></i>
                            </div>
                            <div class="alert-body">
                                <h2><strong>Attention:</strong> You Have Expired Courses</h2>
                                <p>You have <strong>{{ expiredCourses.count }} expired {{ expiredCourses.count === 1 ? 'course' : 'courses' }}</strong> that can be reactivated. Please click on Reactivate My Courses to explore your options.</p>
                                <div class="alert-actions">
                                    <router-link to="/reactivation" class="btn-primary">
                                        <i class="fas fa-redo mr-2"></i>Reactivate My Courses
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="columns">
                    <div class="column">
                        <h2><i class="fas fa-arrow-right fa-fw mr-2"></i>Getting Started</h2>

                        <div class="menu">
                            <router-link to="/course-catalog">
                                <div class="menu-item">
                                    <i class="fad fa-browsers"></i>
                                    <span class="menu-title">Browse Course Catalog</span>
                                </div>
                            </router-link>
                            <router-link to="/conference-center">
                                <div class="menu-item">
                                    <i class="fad fa-presentation"></i>
                                    <span class="menu-title">Browse Conference Center</span>
                                </div>
                            </router-link>
                            <router-link to="/enrollments">
                                <div class="menu-item">
                                    <i class="fad fa-chalkboard-teacher"></i>
                                    <span class="menu-title">Access Your Enrollments</span>
                                </div>
                            </router-link>
                            <router-link to="/registrations">
                                <div class="menu-item">
                                    <i class="fad fa-screen-users"></i>
                                    <span class="menu-title">Access Your Conferences</span>
                                </div>
                            </router-link>
                            <!--                            <router-link to="/reports">-->
                            <!--                                <div class="menu-item">-->
                            <!--                                    <i class="fad fa-file-certificate"></i>-->
                            <!--                                    <span class="menu-title">Manage Your Certificates</span>-->
                            <!--                                </div>-->
                            <!--                            </router-link>-->

                        </div>
                    </div>
<!--                    <div class="column is-4 is-hidden-touch is-flex-desktop">-->
<!--                        <card-header title="Keep Working" icon="fa-history"/>-->

<!--                        <card-body>-->
<!--                            <card-list>-->
<!--                                <container-list-item padded>-->

<!--                                </container-list-item>-->
<!--                            </card-list>-->
<!--                        </card-body>-->
<!--                    </div>-->
                </div>


                <!--                    <div class="card mt-5" style="height: 2000px;">-->
                <!--                        <div class="card-content">-->
                <!--                            Card-->

                <!--                        </div>-->
                <!--                    </div>-->

                <!--                    <div class="columns">-->
                <!--                        <div class="column is-4">-->


                <!--                            <div class="mb-5">-->
                <!--                                <card-header title="Getting Started" icon="fa-hand-point-right"/>-->

                <!--                                <card-body>-->
                <!--                                    <card-list>-->
                <!--                                        <container-list-item padded>-->

                <!--                                        </container-list-item>-->
                <!--                                    </card-list>-->
                <!--                                </card-body>-->
                <!--                            </div>-->

                <!--                            <div class="mb-5">-->
                <!--                                <card-header title="Keep Working" icon="fa-history"/>-->

                <!--                                <card-body>-->
                <!--                                    <card-list>-->
                <!--                                        <container-list-item padded>-->

                <!--                                        </container-list-item>-->
                <!--                                    </card-list>-->
                <!--                                </card-body>-->
                <!--                            </div>-->

                <!--                            <div class="mb-5">-->
                <!--                                <card-header title="Pending Tasks" icon="fa-tasks"/>-->

                <!--                                <card-body>-->
                <!--                                    <card-list>-->
                <!--                                        <container-list-item padded>-->

                <!--                                        </container-list-item>-->
                <!--                                    </card-list>-->
                <!--                                </card-body>-->
                <!--                            </div>-->

                <!--                        </div>-->

                <!--                    </div>-->
            </div>
        </template>
    </framework>
</template>

<script>
    import {mapGetters} from "vuex";
    import {client as http} from "@/http_client";
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";

    export default {
        components: {Framework, SidePanelNav, MainHeader},
        data() {
            return {
                paymentPlans: [],
                cartQuotes: [],
                expiredCourses: { hasExpired: false, count: 0 }
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            welcome: function() {
                const currentHour = new Date().getHours();
                return currentHour >= 4 && currentHour < 12 ? 'Good morning' :
                    currentHour >= 12 && currentHour <= 17 ? 'Good afternoon' :
                        currentHour > 17 || currentHour < 4 ? 'Good evening' : 'Welcome';
            },
            hasNonOnboardedPlan: function() {
                // Show alert if TPO plan exists and is not fully complete
                return this.paymentPlans.some(plan =>
                    plan.is_tpo === true &&
                    plan.is_tpo_complete === false
                );
            },
            needsOnboarding: function() {
                // They haven't started onboarding yet - need link
                return this.paymentPlans.some(plan =>
                    plan.is_tpo === true &&
                    plan.is_tpo_onboarded === false &&
                    plan.is_tpo_complete === false
                );
            },
            needsDocuSign: function() {
                // They've started onboarding but need to complete DocuSign
                return this.paymentPlans.some(plan =>
                    plan.is_tpo === true &&
                    plan.is_tpo_onboarded === true &&
                    plan.is_tpo_complete === false
                );
            },
            tpoOnboardingUrl: function() {
                const nonOnboardedPlan = this.paymentPlans.find(plan =>
                    plan.is_tpo === true &&
                    plan.is_tpo_onboarded === false &&
                    plan.tpo_onboard_hash
                );
                return nonOnboardedPlan
                    ? `https://app.thepaymentoption.com/#/agreement/${nonOnboardedPlan.tpo_onboard_hash}`
                    : '';
            },
            hasCartEnrollment: function() {
                return this.cartQuotes.length > 0;
            },
            cartQuote: function() {
                return this.cartQuotes.length > 0 ? this.cartQuotes[0] : {};
            },
            courseCount: function() {
                if (!this.cartQuote.course_choices) return 0;
                const courses = typeof this.cartQuote.course_choices === 'string'
                    ? JSON.parse(this.cartQuote.course_choices)
                    : this.cartQuote.course_choices;
                return courses ? courses.length : 0;
            },
            hasExpiredCourses: function() {
                return this.expiredCourses.hasExpired && this.expiredCourses.count > 0;
            },
            hasUnpaidSalesTax: function() {
                return this.paymentPlans.some(plan =>
                    plan.is_tpo_complete &&
                    parseFloat(plan.tax_amount) > 0 &&
                    (plan.tax_paid === null || parseFloat(plan.tax_paid) === 0)
                );
            }
        },
        mounted() {
            if (!localStorage.getItem('token')) this.$router.push('/');
            this.loadPaymentPlans();
            this.loadCartQuotes();
            this.loadExpiredCourses();
        },
        methods: {
            loadPaymentPlans() {
                http.get('app/users/payment-plans').then(response => {
                    this.paymentPlans = response.data;
                }).catch(err => {
                    console.error('Error loading payment plans:', err);
                });
            },
            loadCartQuotes() {
                http.get('app/users/cart-quotes').then(response => {
                    this.cartQuotes = response.data;
                }).catch(err => {
                    console.error('Error loading cart quotes:', err);
                });
            },
            loadExpiredCourses() {
                http.get('app/users/expired-courses').then(response => {
                    this.expiredCourses = response.data;
                }).catch(err => {
                    console.error('Error loading expired courses:', err);
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .tpo-onboarding-alert {
        background-color: white;
        border-radius: 0.5rem;
        padding: 0.5rem;
        margin-bottom: 4rem;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);

        .alert-inner {
            border: 2px solid #ffc107;
            border-radius: 0.375rem;
            padding: 1.5rem;
        }

        &.cart-alert {
            margin-bottom: 5rem;

            .alert-inner {
                border-color: #ffc107;
            }
        }

        &.tax-alert {

            .alert-inner {
                border-color: #dc3545;
            }

            .alert-icon {
                color: #dc3545 !important;
            }

            .alert-body h2 strong {
                color: #dc3545 !important;
            }

            .alert-actions .btn-primary {
                background: #dc3545 !important;
                color: #fff !important;

                &:hover {
                    background: #c82333 !important;
                }
            }
        }

        .alert-content {
            display: flex;
            align-items: flex-start;
            gap: 1.25rem;

            .alert-icon {
                font-size: 2rem;
                color: #ffc107;
                flex-shrink: 0;
                margin-top: -0.5rem;
            }

            .alert-body {
                flex: 1;

                h2 {
                    font-size: 1.15rem;
                    color: #333;
                    margin-bottom: 0.5rem;
                    font-weight: 600;

                    strong {
                        color: #d49a00;
                    }
                }

                p {
                    color: #555;
                    font-size: 0.95rem;
                    line-height: 1.5;
                    margin-bottom: 1.25rem;
                }

                .alert-actions {
                    display: flex;
                    gap: 0.75rem;
                    flex-wrap: wrap;

                    a {
                        text-decoration: none;
                        padding: 0.65rem 1.25rem;
                        border-radius: 0.375rem;
                        font-weight: 500;
                        transition: all 0.15s ease;
                        display: inline-flex;
                        align-items: center;
                        font-size: 0.9rem;

                        &.btn-primary {
                            background: #ffc107;
                            color: #1e3a5f;

                            &:hover {
                                background: #e6ad00;
                                transform: translateY(-1px);
                            }
                        }

                        &.btn-secondary {
                            background: transparent;
                            color: #666;
                            border: 1px solid #ccc;
                            font-size: 0.85rem;

                            &:hover {
                                background: #f5f5f5;
                                color: #333;
                                border-color: #999;
                            }
                        }
                    }
                }
            }
        }

        @media (max-width: 768px) {
            padding: 0.375rem;

            .alert-inner {
                padding: 1.25rem;
            }

            .alert-content {
                gap: 1rem;

                .alert-icon {
                    font-size: 1.5rem;
                }

                .alert-body {
                    h2 {
                        font-size: 1.05rem;
                    }

                    p {
                        font-size: 0.9rem;
                    }

                    .alert-actions {
                        flex-direction: column;
                        gap: 0.5rem;

                        a {
                            width: 100%;
                            justify-content: center;
                        }
                    }
                }
            }
        }
    }

    .start {
        display: flex;
        flex-direction: column;

        transition-delay: 0ms;
        transition-duration: 500ms;

        h2 {
            font-size: 1.2rem;
            color: white;
            padding-bottom: 0.5rem;
            margin-bottom: 1rem;
            border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

            i {
                color: white;
            }
        }

        .welcome {
            color: white;
            margin: 3rem 0;

            h1 {
                font-size: 2.5rem;
                color: #ffffff;

                strong {
                    color: #ffffff;
                }
            }

            h3 {
                font-size: 1.2rem;
                color: rgba(255, 255, 255, 0.7);
            }

            @media (max-width: 600px) {
                margin: 1rem 0 2rem;

                h1 {
                    font-size: 1.7rem;
                }
                h3 {
                    font-size: 1rem;
                }
            }
        }

        .menu {
            display: flex;
            flex-wrap: wrap;
            /*justify-content: space-between;*/

            .menu-item {
                border: 1px solid rgba(255, 255, 255, 0.1);
                color: white;
                text-align: center;
                padding: 1.8rem 1rem 1rem;
                margin: 1rem 1.1rem 0 0;
                border-radius: 1.1rem;
                position: relative;
                width: 10rem;
                cursor: pointer;
                transition: all linear 0.15s;

                &:hover {
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid rgba(255, 255, 255, 1);
                }

                i {
                    font-size: 3.1rem;
                    margin-bottom: 0.3rem;
                }

                .menu-title {
                    display: block;
                    padding-top: 0.45rem;
                    font-size: 0.85rem;
                }

                .menu-tag {
                    position: absolute;
                    top: 0.5rem;
                    right: 0.5rem;
                    background-color: #f9b000;
                    color: #0e2703;
                    padding: 0.2rem 0.45rem 0.1rem;
                    font-size: 11px;
                    font-weight: bold;
                    border-radius: 10px;
                }

                @media (max-width: 600px) {
                    width: 100%;
                    text-align: left;
                    padding: 1rem 2rem;

                    i {
                        font-size: 1.5rem;
                        vertical-align: middle;

                    }

                    .menu-title {
                        display: inline-block;
                        padding: 0 0 0 1rem;
                        margin: 0;
                        font-size: 1rem;
                    }
                }

                @media (max-width: 600px) {
                    margin: 0 1rem 1rem 0;
                    padding: 1.2rem 2rem 1rem 1.8rem;
                }
            }

            @media (max-width: 600px) {
                margin: 0;
                padding: 0 0 0 0;
                display: block;
            }
        }
    }
</style>
